import {StorageInstance} from "~/ts/library/storage/StorageFactory";
import Random from "~/ts/library/Random";
import {
    OPERATOR_STATUS_INVISIBLE,
    OPERATOR_STATUS_OFFLINE,
    OPERATOR_STATUS_ONLINE,
    OperatorStatus
} from "~/chat/ts/data/OperatorStatus";
import {toggleArrayElement} from "~/ts/library/ToggleArrayElement";
import ObjectHelper from "~/ts/library/ObjectHelper";
import Dictionary from "~/ts/library/Dictionary";
import {CLIENT_STATUS_OFFLINE, CLIENT_STATUS_ONLINE, ClientStatus} from "~/operatorApplication/ts/data/ClientStatus";
import {
    OPERATOR_STATUS_UPDATED,
    OperatorApplicationEventManager,
    OperatorStatusUpdatedEventPayload
} from "~/operatorApplication/ts/OperatorApplicationEventManager";
import {OPERATOR_APPLICATION_RELOAD_DATA_EVENT} from "~/operatorApplication/ts/service/comet/Constants";
import {computed, ref} from "vue";
import StringHelper from "~/ts/library/StringHelper";
import {getOperatorStatusList} from "~/operatorApplication/ts/data/OperatorStatus";
import {ClientDialogInitActionResponseInterface} from "~/operatorApplication/ts/OperatorApplicationIframeSender";
import prepareTextForSearch from "~/operatorApplication/ts/service/dialog/prepareTextForSearch";
import OperatorGetParamsSiteOpenApiModel = OperatorApplicationApi.OperatorGetParamsSiteOpenApiModel;
import OperatorGetParamsCrossOperatorGroupOpenApiModel = OperatorApplicationApi.OperatorGetParamsCrossOperatorGroupOpenApiModel;

class OperatorAccount {
    public cabinetClientDialog = ref<ClientDialogInitActionResponseInterface>();
    public cabinetReportsOperatorLogin = computed(() => this.cabinetClientDialog.value?.login);
    public login = ref<string>();
    public password = ref<string>();
    public data = ref<OperatorApplicationApi.OperatorApplicationParamsOpenApiModel>();
    public storedStatusId = ref<OperatorStatus>();
    public storedCustomStatusId = ref<string>();
    public isAutoAway = ref(false);
    public isAutoInvisible = ref(false);
    public logoutError = ref<string>();
    public sessionUid = ref<string>();
    public isOperatorParamsLoading = ref(false);
    public isCometConnected = ref(false);
    public crossOperatorChat = ref<Dictionary<CrossOperatorChat>>({});
    public unreadedNewsCount = ref(0);
    public isWorkingNow = ref(true);
    showFreeDialogForSiteId = ref<string>();
    public buddyList = ref<string[]>([]);
    public messageTemplates = ref<CometOperatorApplication.OperatorGetParamsMessageTemplatesOpenApiModel>();
    public messageTemplatesBySite = ref<Dictionary<CometOperatorApplication.OperatorGetParamsMessageTemplatesOpenApiModel>>();
    public credentialsForAuthAfterLoad = ref<{ login: string, password: string }>();
    public minimizeAfterStartup = ref(false);
    public outOfDialogsLimitSiteIds = ref<string[]>([]);

    get isServerSideSettings() {
        return !!this.data.value?.applicationSettings;
    }



    get isCabinetIframe() {
        return !!OperatorAccountStore.cabinetClientDialog.value;
    }

    readonly isAuthorized = computed(() => {
        return typeof this.login.value == "string";
    });
    readonly status = computed<{ id: OperatorStatus, customId?: string }>(() => {
        if (this.isAutoAway.value) {
            return {id: OPERATOR_STATUS_OFFLINE};
        } else if (this.data.value && !this.data.value.isWorkingNow && this.storedStatusId.value == OPERATOR_STATUS_ONLINE) {
            return {id: OPERATOR_STATUS_INVISIBLE};
        }
        return {id: this.storedStatusId.value, customId: this.storedCustomStatusId.value};
    });
    readonly operator = computed<OperatorApplicationApi.OperatorGetParamsOperatorOpenApiModel>(() => {
        return this.data.value?.operators.find(operator => operator.id == this.data.value?.id);

    });
    readonly integrationPlatformDictionary = computed<Dictionary<OperatorApplicationApi.OperatorGetParamsPlatformOpenApiModel>>(() => {
        return this.data.value ? ObjectHelper.dictionaryFromArray(this.data.value.platforms) : {};
    });
    readonly messageSourceTypeDictionary = computed<Dictionary<OperatorApplicationApi.OperatorApplicationMessageSourceTypeOpenApiModel>>(() => {
        return this.data.value ? ObjectHelper.dictionaryFromArray(this.data.value.messageSourceTypes) : {};
    });
    readonly tokenId = computed(() => {
        let token = StorageInstance.get("tokenId");
        if (!token) {
            token = Random.uid();
            StorageInstance.set("tokenId", token);
        }
        if (this.isCabinetIframe) {
            return `cabinet_${token}`;
        }
        return token;
    });
    readonly operatorsDictionary = computed<Dictionary<OperatorApplicationApi.OperatorGetParamsOperatorOpenApiModel>>(() => {
        if (this.data.value) {
            return ObjectHelper.dictionaryFromArray(this.data.value.operators, "login");
        } else {
            return {};
        }
    });

    readonly buddyListConfigDictionary = computed<Dictionary<OperatorApplicationApi.OperatorBuddyListConfigOpenApiModel>>(() => {
        if (this.data.value) {
            return ObjectHelper.dictionaryFromArray(this.data.value.buddyListConfig, "id");
        } else {
            return {};
        }
    });

    readonly sitesDictionary = computed<Dictionary<OperatorApplicationApi.OperatorGetParamsSiteOpenApiModel>>(() => {
        if (this.data.value) {
            return ObjectHelper.dictionaryFromArray(this.data.value.sites, "id");
        } else {
            return {};
        }
    });
    readonly customStatusDictionary = computed<Dictionary<OperatorApplicationApi.CustomOperatorStatusOpenApiModel>>(() => {
        if (this.data.value) {
            return ObjectHelper.dictionaryFromArray(this.data.value.customStatusList, "id");
        } else {
            return {};
        }
    });
    readonly messageTypeDictionary = computed<Dictionary<OperatorApplicationApi.OperatorApplicationMessageTypeOpenApiModel>>(() => {
        if (this.data.value) {
            return ObjectHelper.dictionaryFromArray(this.data.value.messageTypes, "id");
        } else {
            return {};
        }
    });

    public getCrossOperatorsChatClientStatusByOperator(operator: OperatorApplicationApi.OperatorGetParamsOperatorOpenApiModel): ClientStatus {
        return operator.statusId != OPERATOR_STATUS_OFFLINE ? CLIENT_STATUS_ONLINE : CLIENT_STATUS_OFFLINE;
    }

    readonly optionsWithIconBySite = computed(() => {
        let result: Dictionary<Dictionary<{
            field: OperatorApplicationApi.AbstractFormElementOpenApiModel,
            options: Dictionary<OperatorApplicationApi.FormElementOptionOpenApiModel>
        }>> = {};
        if (this.data.value.sites) {
            for (let site of this.data.value.sites) {
                result[site.id] = {};
                for (let field of site.userFields as OperatorApplicationApi.FormElementSelectOpenApiModel[]) {
                    let options: OperatorApplicationApi.FormElementOptionOpenApiModel[] = [];
                    if (field.options) {
                        options.push(...field.options.filter(option => !!option.icon));
                    }
                    if (field.optgroup) {
                        for (let optgroup of field.optgroup) {
                            options.push(...optgroup.options.filter(option => !!option.icon));
                        }
                    }
                    if (options.length) {
                        result[site.id][field.name] = {
                            field,
                            options: {}
                        };
                        for (let option of options) {
                            result[site.id][field.name].options[option.value] = option;
                        }
                    }
                }

            }
        }
        return result;
    });
    readonly flagsDictionary = computed<Dictionary<OperatorApplicationApi.OperatorApplicationClientFlagOpenApiModel>>(() => {
        if (this.data.value) {
            return ObjectHelper.dictionaryFromArray(this.data.value.flags, "id");
        } else {
            return {};
        }
    });
    readonly orderFieldsDictionary = computed<Dictionary<OperatorApplicationApi.OperatorBuddyListOrderFieldOpenApiModel>>(() => {
        if (this.data.value) {
            return ObjectHelper.dictionaryFromArray(this.data.value.buddyListOrderFields, "id");
        } else {
            return {};
        }
    });
    readonly templatesForSearch = computed<Dictionary<{
        search: string,
        template: OperatorApplicationApi.OperatorGetParamsMessageTemplateOpenApiModel
    }[]>>(() => {
        let result: Dictionary<{
            search: string,
            template: OperatorApplicationApi.OperatorGetParamsMessageTemplateOpenApiModel
        }[]> = {};

        if (this.data.value.sites) {
            for (let site of this.data.value.sites) {
                let siteTemplates = [];
                for (let template of this.getMessageTemplatesBySite(site.id).templates) {
                    if (!template.systemTypeId) {
                        let categories: string[] = [];
                        if (this.messageTemplates.value?.categories && template.categoryIds) {
                            categories = template.categoryIds
                                .map(categoryId => this.messageTemplates.value.categories.find(category => category.id == categoryId)?.descr)
                                .filter(categoryDescr => typeof categoryDescr == "string");
                        }
                        siteTemplates.push({
                            search: prepareTextForSearch((categories.join("; ") + "; " + template.descr + "; " + StringHelper.stripBbCode(template.text ?? "") + "; " + template.html)),
                            template
                        });
                    }
                }
                result[site.id] = siteTemplates;
            }
        }

        return result;
    });

    SET_CREDENTIALS_FOR_AUTH_AFTER_LOAD(payload?: { login: string, password: string }) {
        this.credentialsForAuthAfterLoad.value = payload;
    }

    SET_MINIMIZE_AFTER_STARTUP(payload: boolean) {
        this.minimizeAfterStartup.value = payload;
    }

    async logIn(p: { login: string, password: string }) {
        this.SET_CREDENTIALS(p);
    }

    SET_SHOW_FREE_DIALOG_FOR_SITE_ID(siteId?: string) {
        this.showFreeDialogForSiteId.value = siteId;
    }

    get hasNonFreeSites() {
        let result = false;
        let sites = this.data.value?.sites;
        if (sites) {
            for (let site of this.data.value.sites) {
                if (!site.isFree) {
                    result = true;
                    break;
                }
            }
        }
        return result;
    }

    showFreeDialogForSite(siteId: string): boolean {
        let site = this.sitesDictionary.value[siteId];
        if (site && site.isFree) {
            this.SET_SHOW_FREE_DIALOG_FOR_SITE_ID(siteId);
            return true;
        }
        return false;
    }

    initCrossOperatorChat() {
        let result: Dictionary<CrossOperatorChat> = {};
        if (!OperatorAccountStore.isCabinetIframe) {
            if (this.data.value) {
                for (let group of this.data.value.crossOperatorGroups) {
                    result[group.chatId] = {
                        descr: group.descr,
                        avatar: group.avatarUrl,
                        group,
                        statusGetter: () => CLIENT_STATUS_ONLINE,
                        siteIds: []
                    }
                }

                for (let operator of this.data.value.operators) {
                    if (operator.chatId) {
                        let sitesOfOperator = this.data.value.sites.filter(site => site.operators.includes(operator.login));
                        let groupsOfOperator = this.data.value.crossOperatorGroups.filter(group => group.operatorLogins.includes(operator.login));
                        if (sitesOfOperator.length || groupsOfOperator.length) {
                            let descr = operator.fio;
                            result[operator.chatId] = {
                                descr,
                                login: operator.login,
                                avatar: operator.avatar,
                                statusGetter: () => {
                                    return this.getCrossOperatorsChatClientStatusByOperator(operator);
                                },
                                siteIds: sitesOfOperator.map(site => site.id)
                            };
                        }
                    }
                }
            }
        }
        this.crossOperatorChat.value = result;
    }

    public setOperatorParamsLoading(value: boolean) {
        this.isOperatorParamsLoading.value = value;
    }

    public setCometConnected(value: boolean) {
        this.isCometConnected.value = value;
    }

    SET_UNREADED_NEWS_COUNT(count: number) {
        this.unreadedNewsCount.value = count;
    }

    logOut(error?: string) {
        this.SET_AUTO_AWAY(false);
        this.SET_LOGOUT_ERROR(error);
        this.SET_CREDENTIALS();
        this.SET_UNREADED_NEWS_COUNT(0);
        this.CLEAR_BUDDY_LIST();
        this.SET_DATA();
        this.CLEAR_OUT_OF_DIALOGS_LIMIT_SITES();
        this.UPDATE_MESSAGE_TEMPLATES(null);
    }

    CLEAR_OUT_OF_DIALOGS_LIMIT_SITES() {
        this.outOfDialogsLimitSiteIds.value.splice(0, this.outOfDialogsLimitSiteIds.value.length);
    }

    TOGGLE_OUT_OF_DIALOGS_LIMIT_ON_SITE(payload: { siteId: string, value: boolean }) {
        toggleArrayElement(this.outOfDialogsLimitSiteIds.value, payload.siteId, payload.value);
    }

    CLEAR_BUDDY_LIST() {
        this.buddyList.value.splice(0, this.buddyList.value.length);
    }

    setLogoutError(error?: string) {
        this.SET_LOGOUT_ERROR(error);
    }

    getOperatorStatus(login: string, siteId?: string): { statusId: OperatorStatus, customStatusId?: string } {
        let statusId: OperatorStatus = 0;
        let customStatusId: string;
        let operator = this.operatorsDictionary.value[login];
        if (operator) {
            statusId = operator.statusId as OperatorStatus;
            customStatusId = operator.customStatusId;
            if (statusId != OPERATOR_STATUS_OFFLINE) {
                if (siteId) {
                    let site = this.sitesDictionary.value[siteId];
                    if (site) {
                        if (site.unavailableBecauseOfLicenseLimitList.includes(login)) {
                            statusId = OPERATOR_STATUS_OFFLINE;
                            customStatusId = null;
                        }
                    }
                }
            }
        }
        return {statusId: statusId, customStatusId};
    }

    getOperatorsOfSite(site: OperatorGetParamsSiteOpenApiModel) {
        return this.data.value?.operators.filter(item => site.operators.includes(item.login)) ?? [];
    }

    receiveOperatorsOnlineUpdate(payload: CometOperatorApplication.OperatorsOnlineUpdatedCometMessageOpenApiModel) {
        this.SET_OPERATOR_WORKING_NOW(payload.workingNow);
        this.SET_UNAVAILABLE_BECAUSE_OF_LICENSE({siteId: payload.siteId, value: payload.outOfLicenseLimit});
        for (let login in payload.allSitesData) {
            if (payload.allSitesData.hasOwnProperty(login)) {
                let site = this.sitesDictionary.value[payload.siteId];
                let status = payload.allSitesData[login];
                let customStatusId = payload.customStatusIds[login];
                if (login === this.operator.value.login) {
                    if (status != this.status.value.id) {
                        if (status === OPERATOR_STATUS_OFFLINE) {

                            if (site && !site.isUnavailableBecauseOfLicenseLimit) {
                                OperatorApplicationEventManager.emit(OPERATOR_APPLICATION_RELOAD_DATA_EVENT, JSON.stringify({
                                    "receiveOperatorsOnlineUpdate": true,
                                    payload
                                }));
                            }
                        }
                        OperatorAccountStore.setStatusId({
                            statusId: status,
                            customStatusId
                        });
                    }
                } else {
                }
                this.SET_STATUS_OF_OPERATOR({
                    login,
                    status,
                    customStatusId,
                    isWorkingNow: !payload.notWorkingNowList.includes(login)
                });
                if (site) {
                    let siteList = site.unavailableBecauseOfLicenseLimitList;
                    siteList.splice(0, siteList.length);
                    siteList.push(...payload.unavailableBecauseOfLicenseLimits);
                }
            }
        }
        this.TOGGLE_OUT_OF_DIALOGS_LIMIT_ON_SITE({
            siteId: payload.siteId,
            value: payload.outOfDialogsLimit
        });
    }

    setStatusId(payload: { statusId: OperatorStatus, customStatusId?: string }) {
        this.SET_STATUS(payload.statusId);
        this.SET_CUSTOM_STATUS_ID(payload.customStatusId);
    }

    SET_OPERATOR_DIALOG_COUNT(payload: { login: string, count: Dictionary<number> }) {
        let operator = this.operatorsDictionary.value[payload.login];
        if (operator) {
            operator.dialogCount = payload.count;
            //operator.dialogCount = payload.count;
        }
    }

    setAutoAway(value: boolean) {
        this.SET_AUTO_AWAY(value);
    }

    setAutoInvisible(value: boolean) {
        this.SET_AUTO_INVISIBLE(value);
    }

    setData(data: OperatorApplicationApi.OperatorApplicationParamsOpenApiModel = null) {
        this.SET_DATA(data);
        this.setStatusId({
            statusId: this.data.value.currentStoredStatus as OperatorStatus,
            customStatusId: this.data.value.currentStoredCustomStatus
        });
    }

    TOGGLE_CLIENT_IN_BUDDY_LIST(payload: { username: string, value: boolean }) {
        toggleArrayElement(this.buddyList.value, payload.username, payload.value);
    }

    addClientToBuddyList(username: string) {
        this.TOGGLE_CLIENT_IN_BUDDY_LIST({username, value: true});
    }

    removeClientFromBuddyList(username: string) {
        this.TOGGLE_CLIENT_IN_BUDDY_LIST({username, value: false});
    }

    UPDATE_MESSAGE_TEMPLATES(payload?: CometOperatorApplication.OperatorGetParamsMessageTemplatesOpenApiModel) {
        this.messageTemplates.value = payload;
        this.messageTemplatesBySite.value = null;
    }

    getMessageTemplatesBySite(siteId?: string) {
        if (!this.messageTemplatesBySite.value || !this.messageTemplatesBySite.value[siteId]) {
            this.SET_MESSAGE_TEMPLATE_BY_SITE(siteId);
        }
        return this.messageTemplatesBySite.value[siteId];
    }

    SET_MESSAGE_TEMPLATE_BY_SITE(siteId?: string) {
        if (!this.messageTemplatesBySite.value) {
            this.messageTemplatesBySite.value = {};
        }
        this.messageTemplatesBySite.value[siteId] = {
            templates: this.messageTemplates.value?.templates.filter(template => !template.siteIds || template.siteIds.includes(siteId)) ?? [],
            categories: this.messageTemplates.value?.categories.filter(category => !category.siteIds || category.siteIds.includes(siteId)) ?? [],
        };
    }

    checkAccess(accessValue: boolean, siteId?: string): boolean {
        let site = this.sitesDictionary.value[siteId];
        if (site && site.isFree) {
            accessValue = true;
        }
        return accessValue;
    }

    private SET_CREDENTIALS(p?: { login: string, password: string }) {
        this.sessionUid.value = Random.uid();
        this.login.value = p ? p.login : null;
        this.password.value = p ? p.password : null;
    }

    private SET_LOGOUT_ERROR(error: string) {
        this.logoutError.value = error;
    }

    private SET_DATA(data: OperatorApplicationApi.OperatorApplicationParamsOpenApiModel = null) {
        if (!this.data.value || !data) {
            this.data.value = data;
        } else {
            for (let key in this.data.value) {
                if (this.data.value.hasOwnProperty(key) && !data.hasOwnProperty(key)) {
                    delete (this.data.value as any)[key];
                }
            }

            for (let key in data) {
                if (data.hasOwnProperty(key)) {
                    if (JSON.stringify((data as any)[key]) !== JSON.stringify((this.data.value as any)[key])) {
                        (this.data.value as any)[key] = (data as any)[key];
                    }
                }
            }
        }
        this.initCrossOperatorChat();
    }

    private SET_STATUS(statusId: OperatorStatus) {
        this.storedStatusId.value = statusId;
    }

    private SET_CUSTOM_STATUS_ID(customStatusId?: string) {
        this.storedCustomStatusId.value = customStatusId;
    }

    private SET_AUTO_AWAY(value: boolean) {
        this.isAutoAway.value = value;
    }

    private SET_AUTO_INVISIBLE(value: boolean) {
        this.isAutoInvisible.value = value;
    }

    private SET_STATUS_OF_OPERATOR(payload: {
        login: string,
        status: OperatorStatus,
        customStatusId: string,
        isWorkingNow: boolean
    }) {
        let operator = this.operatorsDictionary.value[payload.login];
        if (operator) {
            let previousVersion = ObjectHelper.jsonClone(operator);
            operator.customStatusId = payload.customStatusId;
            operator.statusId = payload.status;
            operator.isWorkingNow = payload.isWorkingNow;
            let eventPayload: OperatorStatusUpdatedEventPayload = {
                operator, previousVersion
            }
            OperatorApplicationEventManager.emit(OPERATOR_STATUS_UPDATED, eventPayload)
        }
    }

    private SET_OPERATOR_WORKING_NOW(value: boolean) {
        this.data.value.isWorkingNow = value;
    }

    private SET_UNAVAILABLE_BECAUSE_OF_LICENSE(payload: { siteId: string, value: boolean }) {
        let site = this.sitesDictionary.value[payload.siteId];
        if (site) {
            if (site.isUnavailableBecauseOfLicenseLimit !== payload.value) {
                site.isUnavailableBecauseOfLicenseLimit = payload.value;
            }
        }
    }

    public get statusList() {
        return computed(() => {
            let result: IOperatorStatusMenuItem[] = getOperatorStatusList().map(item => ({
                statusId: item.statusId,
                descr: item.descr,
                colorClassName: item.colorClassName
            }));
            for (let status of this.data.value.customStatusList) {
                result.push({
                    statusId: status.realStatusId as OperatorStatus,
                    descr: status.descr,
                    color: status.color,
                    customStatusId: status.id
                });
            }
            return result;
        });
    }
}

export const OperatorAccountStore = new OperatorAccount();


interface CrossOperatorChat {
    descr: string,
    group?: OperatorGetParamsCrossOperatorGroupOpenApiModel,
    login?: string,
    statusGetter: () => ClientStatus,
    avatar?: string,
    siteIds: string[]
}

export interface IOperatorStatusMenuItem {
    statusId: OperatorStatus;
    descr: string;
    color?: string;
    colorClassName?: string;
    customStatusId?: string
}
